import { RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { buildPharmacyNameMap, useScopeTree } from "@/components/scope-picker";
import { Button } from "@/components/ui/button";
import type { ColumnDef } from "@/components/ui/data-table";
import { DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/dialog";
import { ResizableSheet } from "@/components/ui/resizable-sheet";
import { SheetDescription } from "@/components/ui/sheet";
import { pluralWord } from "@/features/synchronizacja-apteki/format";
import { badgeBase, type StatusVariant, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { DEVICE_SECTION_SHORT_LABELS } from "./device-role-config";
import type { DeviceAgent } from "./devices-api";
import {
	deviceLabel,
	isDeviceReachable,
	mergePharmacyNames,
	pharmacyLabelFor,
} from "./devices-helpers";
import {
	type DotnetDeviceState,
	type DotnetInstallBlocker,
	type DotnetInstaller,
	type DotnetInstallOutcome,
	type DotnetInstallResponse,
	type DotnetInstallStatus,
	type DotnetRequirement,
	type DotnetScanOutcome,
	useDotnetInstallMutation,
	useDotnetRuntimeQuery,
	useDotnetScanMutation,
} from "./dotnet-runtime-api";

export type DotnetSheetView = "attention" | "all";

/** Stanowisko apteczne bez kompletu .NET (albo bez skanu) — to liczy baner. */
export function needsDotnetAttention(state: DotnetDeviceState): boolean {
	return state.inScope && state.status !== "ok";
}

interface DotnetRow {
	state: DotnetDeviceState;
	/** Z listy urządzeń; brak = urządzenie spoza listy (nazwa = UUID). */
	device: DeviceAgent | undefined;
	computer: string;
	pharmacy: string;
}

function formatDateTime(value: string): string {
	return new Date(value).toLocaleString("pl-PL", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** Najwyższa zainstalowana wersja wymaganego frameworka danego majora i architektury. */
function installedVersion(state: DotnetDeviceState, req: DotnetRequirement): string | null {
	const versions = state.runtimes
		.filter(
			(r) =>
				r.framework === req.framework &&
				r.arch === req.arch &&
				r.version.split(".")[0] === String(req.major),
		)
		.map((r) => r.version)
		.sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
	return versions[0] ?? null;
}

function runtimeText(state: DotnetDeviceState, req: DotnetRequirement): string {
	if (state.status === "not_scanned") return "—";
	return installedVersion(state, req) ?? "brak";
}

function scanText(state: DotnetDeviceState, scanMinAgentVersion: string): string {
	if (state.scanPending) return "skanowanie…";
	if (state.lastScanError) return `błąd skanu: ${state.lastScanError}`;
	if (state.scannedAt) return formatDateTime(state.scannedAt);
	if (!state.canScan) return `wymaga aktualizacji agenta (≥ ${scanMinAgentVersion})`;
	return "nie przeskanowano";
}

const INSTALL_STATUS: Record<DotnetInstallStatus, { label: string; variant: StatusVariant }> = {
	pending: { label: "oczekuje", variant: "info" },
	sent: { label: "w toku", variant: "info" },
	succeeded: { label: "zainstalowano", variant: "success" },
	failed: { label: "błąd", variant: "error" },
	expired: { label: "wygasło", variant: "warning" },
};

function installText(state: DotnetDeviceState): string {
	const install = state.install;
	if (!install) return "—";
	const label = INSTALL_STATUS[install.status]?.label ?? install.status;
	return install.rebootRequired ? `${label}, wymaga restartu` : label;
}

function installTitle(state: DotnetDeviceState): string | undefined {
	const install = state.install;
	if (!install) return undefined;
	return [
		install.error,
		install.versions.length > 0 ? `Wersje: ${install.versions.join(", ")}` : null,
		`Zlecono: ${formatDateTime(install.requestedAt)}`,
		install.finishedAt ? `Zakończono: ${formatDateTime(install.finishedAt)}` : null,
	]
		.filter(Boolean)
		.join("\n");
}

/** Powód blokady instalacji — `null` = można instalować. */
export function installBlockReason(
	state: DotnetDeviceState,
	reachable: boolean,
	installMinAgentVersion: string,
): string | null {
	const labels: Record<DotnetInstallBlocker, string> = {
		server_group: "serwery bez instalacji",
		agent_outdated: `wymaga aktualizacji agenta (≥ ${installMinAgentVersion})`,
		not_scanned: "najpierw skan",
		nothing_missing: "nic nie brakuje",
		install_in_progress: "instalacja w toku",
	};
	if (state.installBlocker) {
		return labels[state.installBlocker] ?? state.installBlocker;
	}
	if (state.missingMajors.length === 0) return labels.nothing_missing;
	if (!reachable) return "offline";
	return null;
}

function rowInstallBlockReason(row: DotnetRow, installMinAgentVersion: string): string | null {
	const reachable = row.device != null && isDeviceReachable(row.device);
	return installBlockReason(row.state, reachable, installMinAgentVersion);
}

function scanBlockReason(row: DotnetRow, scanMinAgentVersion: string): string | null {
	if (!row.state.canScan) return `wymaga aktualizacji agenta (≥ ${scanMinAgentVersion})`;
	if (row.state.scanPending) return "skanowanie w toku";
	return null;
}

function installersFor(rows: DotnetRow[], installers: DotnetInstaller[]): DotnetInstaller[] {
	const majors = new Set(rows.flatMap((r) => r.state.missingMajors));
	return installers.filter((i) => majors.has(i.major)).sort((a, b) => a.major - b.major);
}

const SCAN_OUTCOME_LABEL: Record<DotnetScanOutcome, string> = {
	sent: "wysłano",
	queued: "w kolejce (offline)",
	in_progress: "skan już trwa",
	agent_outdated: "za stary agent",
	not_found: "nie znaleziono",
};
const SCAN_DONE = new Set<string>(["sent", "queued"]);

const INSTALL_OUTCOME_LABEL: Record<DotnetInstallOutcome, string> = {
	sent: "wysłano",
	error: "błąd wysyłki",
	offline: "offline",
	agent_outdated: "za stary agent",
	server_group: "serwer",
	not_scanned: "bez skanu",
	nothing_missing: "nic nie brakuje",
	in_progress: "instalacja już trwa",
	not_found: "nie znaleziono",
};
const INSTALL_DONE = new Set<string>(["sent"]);

/** „wysłano: 3, w kolejce: 1; pominięto 2 (offline: 1, za stary agent: 1)”. */
export function summarizeOutcomes(
	outcomes: string[],
	labels: Record<string, string>,
	done: ReadonlySet<string>,
): { ok: boolean; text: string } {
	const counts = new Map<string, number>();
	for (const o of outcomes) counts.set(o, (counts.get(o) ?? 0) + 1);
	const entries = [...counts.entries()];
	const part = ([o, n]: [string, number]) => `${labels[o] ?? o}: ${n}`;
	const sent = entries.filter(([o]) => done.has(o));
	const skipped = entries.filter(([o]) => !done.has(o));
	const skippedTotal = skipped.reduce((sum, [, n]) => sum + n, 0);
	const text = [
		sent.length > 0 ? sent.map(part).join(", ") : "nic nie wysłano",
		skippedTotal > 0 ? `pominięto ${skippedTotal} (${skipped.map(part).join(", ")})` : null,
	]
		.filter(Boolean)
		.join("; ");
	return { ok: sent.length > 0, text };
}

function notify(prefix: string, summary: { ok: boolean; text: string }) {
	(summary.ok ? toast.success : toast.warning)(`${prefix}: ${summary.text}`);
}

/** Toast po zleceniu instalacji — arkusz i wiersz w „Poleceniach” kafelka. */
export function notifyInstallResult(res: DotnetInstallResponse) {
	notify(
		"Instalacja .NET",
		summarizeOutcomes(
			res.results.map((r) => r.outcome),
			INSTALL_OUTCOME_LABEL,
			INSTALL_DONE,
		),
	);
}

function RuntimeCell({ row, req }: { row: DotnetRow; req: DotnetRequirement }) {
	const text = runtimeText(row.state, req);
	return (
		<span
			className={cn(
				"font-mono text-xs tabular-nums",
				text === "brak" && "font-sans font-medium text-status-error-text",
				text === "—" && "text-muted-foreground",
			)}
		>
			{text}
		</span>
	);
}

interface DotnetRuntimeSheetProps {
	devices: DeviceAgent[];
	initialView: DotnetSheetView;
	onClose: () => void;
}

/**
 * Stan .NET Desktop Runtime x86 na komputerach z agentem + ręczny skan i cicha
 * instalacja brakujących wersji. Widok „Wymagające interwencji” = stanowiska
 * apteczne bez kompletu; „Wszystkie” = każda rola (ręczna instalacja działa też
 * na franczyzie i laptopach — backend blokuje tylko grupę „Serwery”).
 */
export function DotnetRuntimeSheet({ devices, initialView, onClose }: DotnetRuntimeSheetProps) {
	const { data: overview, isLoading, isError } = useDotnetRuntimeQuery(true);
	const { data: scopeTree } = useScopeTree();
	const { mutate: mutateScan, isPending: scanPending } = useDotnetScanMutation();
	const install = useDotnetInstallMutation();
	const [view, setView] = useState<DotnetSheetView>(initialView);
	const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
	const [confirmTargets, setConfirmTargets] = useState<DotnetRow[] | null>(null);

	const pharmacyNames = useMemo(
		() => mergePharmacyNames(buildPharmacyNameMap(scopeTree), devices),
		[scopeTree, devices],
	);

	const allRows = useMemo<DotnetRow[]>(() => {
		const byUuid = new Map(devices.map((d) => [d.deviceUuid, d]));
		return (overview?.devices ?? [])
			.map((state) => {
				const device = byUuid.get(state.deviceUuid);
				return {
					state,
					device,
					computer: device ? deviceLabel(device) : state.deviceUuid,
					pharmacy: device?.idfirm != null ? pharmacyLabelFor(device, pharmacyNames) : "—",
				};
			})
			.sort(
				(a, b) =>
					a.pharmacy.localeCompare(b.pharmacy, "pl") || a.computer.localeCompare(b.computer, "pl"),
			);
	}, [overview, devices, pharmacyNames]);

	const attentionRows = useMemo(
		() => allRows.filter((r) => needsDotnetAttention(r.state)),
		[allRows],
	);
	const rows = view === "attention" ? attentionRows : allRows;

	const installMin = overview?.installMinAgentVersion ?? "";
	const scanMin = overview?.scanMinAgentVersion ?? "";
	const selectedRows = rows.filter((r) => selected.has(r.state.deviceUuid));
	const scanTargets = selectedRows.filter((r) => !scanBlockReason(r, scanMin));
	const installTargets = selectedRows.filter((r) => !rowInstallBlockReason(r, installMin));

	// `mutate` z React Query jest stabilne, więc kolumny nie przeliczają się co render.
	const runScan = useCallback(
		(targets: DotnetRow[]) =>
			mutateScan(
				targets.map((r) => r.state.deviceUuid),
				{
					onSuccess: (res) =>
						notify(
							"Skan .NET",
							summarizeOutcomes(
								res.results.map((r) => r.outcome),
								SCAN_OUTCOME_LABEL,
								SCAN_DONE,
							),
						),
					onError: (err) => toast.error(`Nie udało się zlecić skanu: ${err.message}`),
				},
			),
		[mutateScan],
	);

	function runInstall(targets: DotnetRow[]) {
		install.mutate(
			targets.map((r) => r.state.deviceUuid),
			{
				onSuccess: notifyInstallResult,
				onError: (err) => toast.error(`Nie udało się zlecić instalacji: ${err.message}`),
				onSettled: () => setConfirmTargets(null),
			},
		);
	}

	const columns = useMemo<ColumnDef<DotnetRow>[]>(() => {
		const required = overview?.required ?? [];
		const installers = overview?.installers ?? [];
		return [
			{
				id: "select",
				size: 36,
				enableSorting: false,
				enableHiding: false,
				enableColumnFilter: false,
				header: ({ table }) => {
					// Tylko wiersze widoczne po filtrach kolumn — ukryte nie dostaną skanu ani instalacji.
					const visible = table.getFilteredRowModel().rows.map((r) => r.original.state.deviceUuid);
					const allVisible = visible.length > 0 && visible.every((uuid) => selected.has(uuid));
					return (
						<input
							type="checkbox"
							aria-label="Zaznacz wszystkie w widoku"
							checked={allVisible}
							ref={(el) => {
								if (el)
									el.indeterminate = !allVisible && visible.some((uuid) => selected.has(uuid));
							}}
							onChange={() => {
								const next = new Set(selected);
								for (const uuid of visible) {
									if (allVisible) next.delete(uuid);
									else next.add(uuid);
								}
								setSelected(next);
							}}
						/>
					);
				},
				cell: ({ row }) => {
					const uuid = row.original.state.deviceUuid;
					const checked = selected.has(uuid);
					return (
						<input
							type="checkbox"
							aria-label={`Zaznacz ${row.original.computer}`}
							checked={checked}
							onChange={() => {
								const next = new Set(selected);
								if (checked) next.delete(uuid);
								else next.add(uuid);
								setSelected(next);
							}}
						/>
					);
				},
			},
			{
				id: "computer",
				accessorFn: (r) => r.computer,
				header: ({ column }) => <DataTableColumnHeader column={column} title="Komputer" />,
				size: 200,
				cell: ({ row }) => <span className="font-medium">{row.original.computer}</span>,
			},
			{
				id: "pharmacy",
				accessorFn: (r) => r.pharmacy,
				header: ({ column }) => <DataTableColumnHeader column={column} title="Apteka" />,
				size: 200,
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "role",
				accessorFn: (r) =>
					r.device
						? (DEVICE_SECTION_SHORT_LABELS[r.device.deviceRole] ?? r.device.deviceRole)
						: "—",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Rola" />,
				size: 110,
			},
			{
				id: "online",
				accessorFn: (r) => (r.device && isDeviceReachable(r.device) ? "online" : "offline"),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Połączenie" />,
				size: 150,
				cell: ({ row }) => {
					const online = row.original.device != null && isDeviceReachable(row.original.device);
					return (
						<span
							className={cn(badgeBase, "border", statusClasses[online ? "success" : "warning"])}
						>
							{online ? "online" : "offline"}
						</span>
					);
				},
			},
			...required.map<ColumnDef<DotnetRow>>((req) => ({
				id: `net-${req.major}-${req.arch}`,
				accessorFn: (r) => runtimeText(r.state, req),
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title={`.NET ${req.major}`} />
				),
				size: 140,
				cell: ({ row }) => <RuntimeCell row={row.original} req={req} />,
			})),
			{
				id: "scan",
				accessorFn: (r) => scanText(r.state, scanMin),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Ostatni skan" />,
				size: 190,
				cell: ({ row }) => {
					const { state } = row.original;
					const text = scanText(state, scanMin);
					return (
						<span
							className={cn(
								"block truncate text-xs",
								state.lastScanError && "text-status-error-text",
								!state.scannedAt && !state.lastScanError && "text-muted-foreground",
							)}
							title={
								state.lastScanAttemptAt
									? `${text}\nOstatnia próba: ${formatDateTime(state.lastScanAttemptAt)}`
									: text
							}
						>
							{text}
						</span>
					);
				},
			},
			{
				id: "install",
				accessorFn: (r) => installText(r.state),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Instalacja" />,
				size: 170,
				cell: ({ row }) => {
					const { state } = row.original;
					if (!state.install) return <span className="text-muted-foreground">—</span>;
					const variant = INSTALL_STATUS[state.install.status]?.variant ?? "info";
					return (
						<span
							className={cn(badgeBase, "border", statusClasses[variant])}
							title={installTitle(state)}
						>
							{installText(state)}
						</span>
					);
				},
			},
			{
				id: "actions",
				header: () => <span className="sr-only">Akcje</span>,
				size: 330,
				enableSorting: false,
				enableHiding: false,
				enableColumnFilter: false,
				cell: ({ row }) => {
					const r = row.original;
					const scanReason = scanBlockReason(r, scanMin);
					const installReason = rowInstallBlockReason(r, installMin);
					const versions = installersFor([r], installers).map((i) => i.version);
					const majors = r.state.missingMajors;
					return (
						<div className="flex min-w-0 items-center gap-1.5">
							<Button
								type="button"
								size="icon"
								variant="ghost"
								className="size-7 shrink-0"
								aria-label={`Skanuj ponownie ${r.computer}`}
								title={scanReason ?? "Skanuj ponownie"}
								disabled={scanReason != null || scanPending}
								onClick={() => runScan([r])}
							>
								<RefreshCw className="size-3.5" aria-hidden />
							</Button>
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="h-7 shrink-0 px-2 text-xs"
								title={versions.length > 0 ? `Wersje: ${versions.join(", ")}` : undefined}
								disabled={installReason != null}
								onClick={() => setConfirmTargets([r])}
							>
								{majors.length > 0 ? `Zainstaluj .NET ${majors.join(" i ")}` : "Zainstaluj .NET"}
							</Button>
							{installReason ? (
								<span className="truncate text-[11px] text-muted-foreground" title={installReason}>
									{installReason}
								</span>
							) : null}
						</div>
					);
				},
			},
		];
	}, [overview, selected, scanMin, installMin, scanPending, runScan]);

	const columnLabels = useMemo(() => {
		const labels: Record<string, string> = {
			computer: "Komputer",
			pharmacy: "Apteka",
			role: "Rola",
			online: "Połączenie",
			scan: "Ostatni skan",
			install: "Instalacja",
		};
		for (const req of overview?.required ?? []) {
			labels[`net-${req.major}-${req.arch}`] = `.NET ${req.major} ${req.arch}`;
		}
		return labels;
	}, [overview]);

	const confirmVersions = confirmTargets
		? installersFor(confirmTargets, overview?.installers ?? [])
		: [];
	const confirmCount = confirmTargets?.length ?? 0;

	return (
		<ResizableSheet
			open
			onOpenChange={(open) => {
				if (!open) onClose();
			}}
			title=".NET Desktop Runtime x86 na komputerach"
			defaultWidth={1180}
			minWidth={600}
			maxWidth={1600}
			scrollable={false}
		>
			<SheetDescription className="sr-only">
				Stan .NET Desktop Runtime x86 na komputerach z agentem, skan i cicha instalacja brakujących
				wersji.
			</SheetDescription>
			<div className="flex h-full flex-col gap-2 p-3">
				<p className="truncate text-xs text-muted-foreground">
					{overview
						? `Wymagane: ${overview.required.map((r) => `.NET ${r.major} ${r.arch}`).join(", ")} (pharmind XF). Skan: agent ≥ ${scanMin}, instalacja: agent ≥ ${installMin}.`
						: "Wymagane .NET Desktop Runtime x86 dla pharmind XF."}
				</p>
				<div className="flex items-center gap-1.5 overflow-x-auto max-md:[&>button]:h-9">
					<Button
						type="button"
						size="sm"
						variant={view === "attention" ? "default" : "outline"}
						className="h-7 shrink-0 px-2.5 text-xs"
						aria-pressed={view === "attention"}
						onClick={() => setView("attention")}
					>
						Wymagające interwencji ({attentionRows.length})
					</Button>
					<Button
						type="button"
						size="sm"
						variant={view === "all" ? "default" : "outline"}
						className="h-7 shrink-0 px-2.5 text-xs"
						aria-pressed={view === "all"}
						onClick={() => setView("all")}
					>
						Wszystkie urządzenia ({allRows.length})
					</Button>
					<span className="mx-1 shrink-0 text-muted-foreground">·</span>
					<Button
						type="button"
						size="sm"
						variant="outline"
						className="h-7 shrink-0 px-2.5 text-xs"
						disabled={scanTargets.length === 0 || scanPending}
						onClick={() => runScan(scanTargets)}
					>
						<RefreshCw className="mr-1 size-3.5" aria-hidden />
						Skanuj ponownie zaznaczone ({scanTargets.length})
					</Button>
					<Button
						type="button"
						size="sm"
						className="h-7 shrink-0 px-2.5 text-xs"
						disabled={installTargets.length === 0}
						onClick={() => setConfirmTargets(installTargets)}
					>
						Zainstaluj na zaznaczonych ({installTargets.length})
					</Button>
					{selectedRows.length > installTargets.length ? (
						<span className="shrink-0 text-[11px] text-muted-foreground">
							{selectedRows.length - installTargets.length} zaznaczonych bez możliwości instalacji
						</span>
					) : null}
				</div>
				{isError ? (
					<p role="alert" className="text-xs text-status-error-text">
						Nie udało się pobrać stanu .NET z urządzeń.
					</p>
				) : null}
				<DataTable
					className="min-h-0 flex-1"
					tableId="zdalny-dostep-dotnet-runtime"
					columns={columns}
					data={rows}
					getRowId={(r) => r.state.deviceUuid}
					totalCount={rows.length}
					isLoading={isLoading}
					enableToolbar
					enableColumnVisibility
					enableExport
					exportFileName="dotnet-runtime"
					columnLabels={columnLabels}
				/>
			</div>
			<ConfirmDialog
				open={confirmTargets != null}
				onOpenChange={(open) => {
					if (!open) setConfirmTargets(null);
				}}
				title={`Zainstalować .NET na ${confirmCount} ${pluralWord(confirmCount, "komputerze", "komputerach", "komputerach")}?`}
				description={`${confirmVersions.map((i) => `.NET Desktop Runtime ${i.version} ${i.arch}`).join(", ")}. Instalacja cicha, bez restartu komputera; .NET 9 nie jest usuwany.`}
				variant="warning"
				confirmLabel="Zainstaluj"
				isLoading={install.isPending}
				onConfirm={() => {
					if (confirmTargets) runInstall(confirmTargets);
				}}
			>
				{confirmTargets && confirmTargets.length <= 5 ? (
					<ul className="list-disc pl-5 text-xs">
						{confirmTargets.map((r) => (
							<li key={r.state.deviceUuid}>
								{r.computer}
								{r.pharmacy !== "—" ? ` — ${r.pharmacy}` : ""}
							</li>
						))}
					</ul>
				) : null}
			</ConfirmDialog>
		</ResizableSheet>
	);
}
